'use client';

import { memo, useMemo } from 'react';
import { motion } from 'framer-motion';

interface AgentSilhouetteProps {
  /** Index for staggered animations */
  index?: number;
  /** Primary accent color */
  accentColor?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Optional class name */
  className?: string;
}

// Abstract geometric patterns for agent silhouettes
const SILHOUETTE_PATTERNS = [
  // Pattern 1: Circular core with orbiting dots
  (color: string) => (
    <svg viewBox="0 0 80 80" className="w-full h-full">
      <defs>
        <linearGradient id="silGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.6" />
          <stop offset="100%" stopColor={color} stopOpacity="0.2" />
        </linearGradient>
      </defs>
      <circle cx="40" cy="40" r="20" fill="url(#silGrad1)" />
      <circle cx="40" cy="40" r="28" fill="none" stroke={color} strokeOpacity="0.3" strokeWidth="1" strokeDasharray="4 4" />
      <circle cx="40" cy="12" r="4" fill={color} fillOpacity="0.5" />
      <circle cx="68" cy="40" r="3" fill={color} fillOpacity="0.4" />
      <circle cx="40" cy="68" r="3.5" fill={color} fillOpacity="0.45" />
    </svg>
  ),
  // Pattern 2: Hexagonal network
  (color: string) => (
    <svg viewBox="0 0 80 80" className="w-full h-full">
      <defs>
        <linearGradient id="silGrad2" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.5" />
          <stop offset="100%" stopColor={color} stopOpacity="0.15" />
        </linearGradient>
      </defs>
      <polygon points="40,10 65,25 65,55 40,70 15,55 15,25" fill="url(#silGrad2)" />
      <polygon points="40,10 65,25 65,55 40,70 15,55 15,25" fill="none" stroke={color} strokeOpacity="0.4" strokeWidth="1" />
      <circle cx="40" cy="40" r="8" fill={color} fillOpacity="0.3" />
      <line x1="40" y1="40" x2="40" y2="10" stroke={color} strokeOpacity="0.3" strokeWidth="1" />
      <line x1="40" y1="40" x2="65" y2="55" stroke={color} strokeOpacity="0.3" strokeWidth="1" />
      <line x1="40" y1="40" x2="15" y2="55" stroke={color} strokeOpacity="0.3" strokeWidth="1" />
    </svg>
  ),
  // Pattern 3: Stacked layers
  (color: string) => (
    <svg viewBox="0 0 80 80" className="w-full h-full">
      <defs>
        <linearGradient id="silGrad3" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.6" />
          <stop offset="100%" stopColor={color} stopOpacity="0.1" />
        </linearGradient>
      </defs>
      <ellipse cx="40" cy="55" rx="28" ry="10" fill={color} fillOpacity="0.15" />
      <ellipse cx="40" cy="45" rx="24" ry="8" fill={color} fillOpacity="0.25" />
      <ellipse cx="40" cy="35" rx="20" ry="7" fill={color} fillOpacity="0.35" />
      <ellipse cx="40" cy="25" rx="16" ry="6" fill="url(#silGrad3)" />
      <circle cx="40" cy="25" r="4" fill={color} fillOpacity="0.6" />
    </svg>
  ),
  // Pattern 4: Radial burst
  (color: string) => (
    <svg viewBox="0 0 80 80" className="w-full h-full">
      <defs>
        <radialGradient id="silGrad4" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={color} stopOpacity="0.5" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="40" cy="40" r="30" fill="url(#silGrad4)" />
      {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
        <line
          key={i}
          x1="40"
          y1="40"
          x2={40 + Math.cos(angle * Math.PI / 180) * 28}
          y2={40 + Math.sin(angle * Math.PI / 180) * 28}
          stroke={color}
          strokeOpacity={0.2 + (i % 2) * 0.2}
          strokeWidth="1.5"
        />
      ))}
      <circle cx="40" cy="40" r="10" fill={color} fillOpacity="0.4" />
      <circle cx="40" cy="40" r="5" fill={color} fillOpacity="0.6" />
    </svg>
  ),
  // Pattern 5: Wave form
  (color: string) => (
    <svg viewBox="0 0 80 80" className="w-full h-full">
      <defs>
        <linearGradient id="silGrad5" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={color} stopOpacity="0.1" />
          <stop offset="50%" stopColor={color} stopOpacity="0.5" />
          <stop offset="100%" stopColor={color} stopOpacity="0.1" />
        </linearGradient>
      </defs>
      <path d="M10,40 Q20,20 30,40 T50,40 T70,40" fill="none" stroke="url(#silGrad5)" strokeWidth="3" />
      <path d="M10,50 Q20,30 30,50 T50,50 T70,50" fill="none" stroke={color} strokeOpacity="0.3" strokeWidth="2" />
      <path d="M10,30 Q20,50 30,30 T50,30 T70,30" fill="none" stroke={color} strokeOpacity="0.3" strokeWidth="2" />
      <circle cx="40" cy="40" r="6" fill={color} fillOpacity="0.5" />
    </svg>
  ),
  // Pattern 6: Diamond grid
  (color: string) => (
    <svg viewBox="0 0 80 80" className="w-full h-full">
      <defs>
        <linearGradient id="silGrad6" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.4" />
          <stop offset="100%" stopColor={color} stopOpacity="0.1" />
        </linearGradient>
      </defs>
      <rect x="25" y="25" width="30" height="30" fill="url(#silGrad6)" transform="rotate(45 40 40)" />
      <rect x="30" y="30" width="20" height="20" fill="none" stroke={color} strokeOpacity="0.5" strokeWidth="1" transform="rotate(45 40 40)" />
      <rect x="35" y="35" width="10" height="10" fill={color} fillOpacity="0.4" transform="rotate(45 40 40)" />
      <circle cx="40" cy="15" r="3" fill={color} fillOpacity="0.3" />
      <circle cx="65" cy="40" r="3" fill={color} fillOpacity="0.3" />
      <circle cx="40" cy="65" r="3" fill={color} fillOpacity="0.3" />
      <circle cx="15" cy="40" r="3" fill={color} fillOpacity="0.3" />
    </svg>
  ),
];

const SIZE_CLASSES = {
  sm: 'w-16 h-20 md:w-20 md:h-24',
  md: 'w-24 h-28 md:w-28 md:h-32',
  lg: 'w-28 h-32 md:w-36 md:h-40',
};

function AgentSilhouetteComponent({
  index = 0,
  accentColor = '#00D4FF',
  size = 'md',
  className = '',
}: AgentSilhouetteProps) {
  const patternIndex = useMemo(() => index % SILHOUETTE_PATTERNS.length, [index]);
  const pattern = SILHOUETTE_PATTERNS[patternIndex];

  // Randomized but deterministic animation values based on index
  const floatDuration = 3 + (index % 3) * 0.5;
  const floatDelay = index * 0.2;
  const rotateAmount = 2 + (index % 2) * 2;

  return (
    <motion.div
      className={`relative ${SIZE_CLASSES[size]} ${className}`}
      initial={{ opacity: 0, y: 20, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.6,
        delay: floatDelay,
        ease: [0.16, 1, 0.3, 1],
      }}
    >
      {/* Glassmorphic card container */}
      <motion.div
        className="absolute inset-0 rounded-2xl overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${accentColor}10, ${accentColor}05)`,
          backdropFilter: 'blur(8px)',
          border: `1px solid ${accentColor}20`,
          boxShadow: `0 8px 32px -8px ${accentColor}20`,
        }}
        animate={{
          y: [-4, 4, -4],
          rotate: [-rotateAmount, rotateAmount, -rotateAmount],
        }}
        transition={{
          duration: floatDuration,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      >
        {/* Shimmer effect */}
        <motion.div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(90deg, transparent, ${accentColor}15, transparent)`,
          }}
          animate={{ x: ['-100%', '200%'] }}
          transition={{
            duration: 2.5,
            repeat: Infinity,
            repeatDelay: 3 + index,
            ease: 'easeInOut',
          }}
        />

        {/* Inner glow */}
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background: `radial-gradient(circle at 30% 30%, ${accentColor}30, transparent 60%)`,
          }}
        />

        {/* SVG Pattern */}
        <div className="absolute inset-2 flex items-center justify-center">
          {pattern(accentColor)}
        </div>

        {/* Coming Soon badge */}
        <div
          className="absolute bottom-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full text-[9px] font-medium tracking-wide uppercase"
          style={{
            background: `${accentColor}20`,
            color: accentColor,
            border: `1px solid ${accentColor}30`,
          }}
        >
          Soon
        </div>
      </motion.div>

      {/* Outer glow pulse */}
      <motion.div
        className="absolute -inset-1 rounded-2xl -z-10"
        style={{
          background: `radial-gradient(circle, ${accentColor}10, transparent 70%)`,
        }}
        animate={{ scale: [1, 1.1, 1], opacity: [0.5, 0.8, 0.5] }}
        transition={{
          duration: floatDuration * 1.5,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
    </motion.div>
  );
}

export const AgentSilhouette = memo(AgentSilhouetteComponent);
